<template>
  <div class="__ReportFilter">
    <CTitle>{{ $t('Usage Month') }}</CTitle>
    <div class="month-section">
      <!--Month selector / Report download button -->
      <CSelector
        width="200px"
        v-model="selectedMonth"
        :options="monthOptions"
        emit-value
        map-options
        :use-input="false"
      />
      <CButton
        icon="get_app"
        round
        flat
        :disable="!isDownloadable"
        :color="isDownloadable ? 'primary' : 'muted'"
        :tooltip="loading ? null : $t('Download the report (.csv)')"
        :loading="loading"
        @click="handleDownloadCsv"
      />
    </div>

    <div class="filter-header">
      <CTitle>{{ $t('Filters') }}</CTitle>
      <div class="horizontal-line" />
      <CButton
        :icon="showFilters ? 'remove_circle_outline' : 'add_circle_outline'"
        round
        flat
        color="primary"
        :tooltip="showFilters ? $t('Hide filters') : $t('Show filters')"
        @click="handleExpandCollapseFilters"
      />
    </div>
    <SlideTransition class="filters-container" v-model="showFilters">
      <!-- Cost group (groupped by account name / tag name) -->
      <CSection class="filter-section">
        <FilterOptions
          v-model="selectedCostGroups"
          :series="costGroupOptions"
          :title="$t('Account / Tag Resource')"
          series-label="name"
          series-id="id"
          series-value="value"
          filterable
          :filter-key="['name']"
          :search-placeholder="$t('Search by {label}.', { label: $t('Account / Tag Resource') })"
          clickable
          dense
          :loading="loading"
          :disabled="loading"
        />
      </CSection>

      <!-- Service -->
      <CSection class="filter-section">
        <FilterOptions
          v-model="selectedServices"
          :series="serviceOptions"
          :title="$t('Service')"
          series-label="name"
          series-id="id"
          series-value="value"
          filterable
          :filter-key="['name']"
          :search-placeholder="$t('Search by {label}.', { label: $t('Service') })"
          clickable
          dense
          :loading="loading"
          :disabled="loading"
        />
      </CSection>

      <!-- Region -->
      <CSection class="filter-section">
        <FilterOptions
          v-model="selectedRegions"
          :series="regionOptions"
          :title="$t('Region')"
          series-label="name"
          series-id="id"
          series-value="value"
          filterable
          :filter-key="['name']"
          :search-placeholder="$t('Search by {label}.', { label: $t('Region') })"
          clickable
          dense
          :loading="loading"
          :disabled="loading"
        />
      </CSection>

      <!-- Operation -->
      <CSection class="filter-section">
        <FilterOptions
          v-model="selectedOperations"
          :series="operationOptions"
          :title="$t('Operation')"
          series-label="name"
          series-id="id"
          series-value="value"
          filterable
          :filter-key="['name']"
          :search-placeholder="$t('Search by {label}.', { label: $t('Operation') })"
          clickable
          dense
          :loading="loading"
          :disabled="loading"
        />
      </CSection>

      <!-- Instance Type -->
      <CSection class="filter-section">
        <FilterOptions
          v-model="selectedInstanceTypes"
          :series="instanceTypeOptions"
          :title="$t('Instance Type')"
          series-label="name"
          series-id="id"
          series-value="value"
          filterable
          :filter-key="['name']"
          :search-placeholder="$t('Search by {label}.', { label: $t('Instance Type') })"
          clickable
          dense
          :loading="loading"
          :disabled="loading"
        />
      </CSection>

      <!-- Resource ID -->
      <CSection class="filter-section">
        <FilterOptions
          v-model="selectedResourceIds"
          :series="resourceIdOptions"
          :title="$t('Resource ID')"
          series-label="name"
          series-id="id"
          series-value="value"
          filterable
          :filter-key="['name']"
          :search-placeholder="$t('Search by {label}.', { label: $t('Resource ID') })"
          clickable
          dense
          :loading="loading"
          :disabled="loading"
        />
      </CSection>
    </SlideTransition>
    <!-- Amount -->
    <div class="__BillingReport_Header_Content_TotalAmount">
      <CLabelValue :label="$t('Monthly total cost')" align="right" small-label>
        <div class="text-lg">
          <CCurrency
            :value="totalAmount"
            :type="currencyType()"
            :loading="loading"
            :tooltip="`${currencySymbol(currencyType())}${totalAmount}`"
          />
        </div>
      </CLabelValue>
    </div>
    <BillingReportBody :data="serviceData" :loading="loading" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapState, mapMutations } from 'vuex';
import { hash } from '@/vuex/modules/billingReports';
import moment from 'moment';
import { sortObject } from '@/lib/sorter';
import { saveAs } from 'file-saver';
import CButton from '@/components/uikit/CButton';
import CSelector from '@/components/uikit/CSelector';
import CTitle from '@/components/uikit/CTitle';
import CLabelValue from '@/components/uikit/CLabelValue';
import CCurrency from '@/components/uikit/CCurrency';
import CSection from '@/components/uikit/CSection';
import BillingReportBody from '@/components/BillingReport/BillingReportBody';
import FilterOptions from './FilterOptions';
import SlideTransition from '@design-system/components/SlideTransition';

import { analyzer } from '@/analyzer';

const messages = {
  en: {
    'Usage Month': 'Usage Month',
    Filters: 'Filters',
    'Search by {label}.': 'Search by {label}.',
    'Monthly total cost': 'Monthly total cost',
    'Download the report (.csv)': 'Download the report (.csv)',
    'Hide filters': 'Hide filters',
    'Show filters': 'Show filters',
    'Account / Tag Resource': 'Account / Tag Resource',
    Service: 'Service',
    Region: 'Region',
    Operation: 'Operation',
    'Instance Type': 'Instance Type',
    'Resource ID': 'Resource ID',
    'Services without resource ID': 'Services without resource ID',
  },
  ja: {
    'Usage Month': '利用月',
    Filters: 'フィルター',
    'Search by {label}.': '{label}で検索',
    'Monthly total cost': '当月の合計金額',
    'Download the report (.csv)': 'レポートデータ(.csv)のダウンロード',
    'Hide filters': 'フィルターを非表示',
    'Show filters': 'フィルターを表示',
    'Account / Tag Resource': 'アカウント / タグリソース',
    Service: 'サービス',
    Region: 'リージョン',
    Operation: 'オペレーション',
    'Instance Type': 'インスタンスタイプ',
    'Resource ID': 'リソースID',
    'Services without resource ID': 'リソースIDのないサービス',
  },
};
export default Vue.extend({
  name: 'ReportFilter',
  i18n: { messages },
  components: {
    CButton,
    CTitle,
    CSelector,
    CLabelValue,
    CCurrency,
    CSection,
    BillingReportBody,
    FilterOptions,
    SlideTransition,
  },
  data() {
    return {
      showFilters: true,
      vendor: this.$store.state.vendor,
      serviceData: [],
      selectedMonth: '',
      isSettingDefaultOptions: false,
      costGroupOptions: [],
      serviceOptions: [],
      regionOptions: [],
      operationOptions: [],
      instanceTypeOptions: [],
      resourceIdOptions: [],
      selectedCostGroupMap: {},
      selectedServiceMap: {},
      selectedRegionMap: {},
      selectedOperationMap: {},
      selectedInstanceTypeMap: {},
      selectedResourceIdMap: {},
      selectedCostGroups: [],
      selectedServices: [],
      selectedRegions: [],
      selectedOperations: [],
      selectedInstanceTypes: [],
      selectedResourceIds: [],
    };
  },
  mounted() {
    this.updateLogtime(Date.now());
    analyzer.track(`Perform: DashboardReportFilterTabCreated`, {
      logtime2: Number(Date.now()),
    });

    this.selectedMonth = this.reportReadyMonth;
    this.serviceData = [];
    this.costGroupOptions = [];
    this.serviceOptions = [];
    this.regionOptions = [];
    this.operationOptions = [];
    this.instanceTypeOptions = [];
    this.resourceIdOptions = [];
    this.selectedCostGroupMap = {};
    this.selectedServiceMap = {};
    this.selectedRegionMap = {};
    this.selectedOperationMap = {};
    this.selectedInstanceTypeMap = {};
    this.selectedResourceIdMap = {};
    this.selectedCostGroups = [];
    this.selectedServices = [];
    this.selectedRegions = [];
    this.selectedOperations = [];
    this.selectedInstanceTypes = [];
    this.selectedResourceIds = [];
  },
  computed: {
    ...mapGetters('user', {
      resourceTypes: 'getResourceTypes',
    }),
    ...mapGetters('billingReports', {
      logtimeInterval: 'getLogtimeInterval',
    }),
    ...mapState({
      reportReadyMonth: state => state.userState.dataReady.report,
    }),
    ...mapState('billingReports', {
      reports: state => state.reports,
      reportRequests: state => state.requests,
    }),
    monthOptions() {
      if (this.vendor === 'azure') {
        return this.$store.getters['monthOptions'](true);
      } else {
        return this.$store.getters['monthOptions'](false);
      }
    },
    selectedDates() {
      if (!this.selectedMonth) {
        return {
          from: '',
          to: '',
        };
      }
      return {
        from: moment(this.selectedMonth)
          .startOf('month')
          .format('YYYY-MM-DD'),
        to: moment(this.selectedMonth)
          .endOf('month')
          .format('YYYY-MM-DD'),
      };
    },
    usageReport() {
      const { from, to } = this.selectedDates;
      return {
        from: from,
        to: to,
        vendor: this.vendor,
        resourceTypes: this.resourceTypes,
      };
    },
    reportHash() {
      return hash(this.usageReport);
    },
    loading() {
      const request = this.reportRequests[this.reportHash];
      if (request && !request.inProgress) {
        console.timeEnd('dashboard loading filters');
        analyzer.track(`Perform: DashboardReportFilterTabComplete`);
      } else {
        analyzer.time_event(`Perform: DashboardReportFilterTabComplete`);
        console.time('dashboard loading filters');
      }
      return request && request.inProgress;
    },
    allData() {
      const allData = this.reports[this.reportHash];
      if (allData) {
        return allData;
      }
      return [];
    },
    totalAmount() {
      if (this.serviceData) {
        let total = 0;
        for (const svc of this.serviceData) {
          total += svc.total;
        }
        return total;
      }
      return 0;
    },
    isDownloadable() {
      return this.serviceData.length > 0;
    },
  },
  watch: {
    usageReport: {
      handler(oldReport, newReport) {
        if (!oldReport || newReport !== oldReport) {
          this.fetchReports();
        }
      },
    },
    allData() {
      if (!this.allData) {
        return;
      }
      analyzer.track(`Perform: DashboardReportFilterTabDataComplete`, {
        logtime2: Number(Date.now()),
        interval2: Number(this.logtimeInterval),
      });
      this.updateLogtime(Date.now());
      analyzer.track(`Perform: DashboardReportFilterTabFiltersCreated`, {
        logtime2: Number(Date.now()),
      });

      this.createFilterOptions();
      this.setDefaultSelectedOptions();
      this.filterServiceData();
    },
    selectedCostGroups() {
      if (this.isSettingDefaultOptions) {
        return;
      }
      this.selectedCostGroupMap = {};
      for (const optId of this.selectedCostGroups) {
        this.selectedCostGroupMap[optId] = true;
      }
      this.filterServiceData();
    },
    selectedServices() {
      if (this.isSettingDefaultOptions) {
        return;
      }
      this.selectedServiceMap = {};
      for (const optId of this.selectedServices) {
        this.selectedServiceMap[optId] = true;
      }
      this.filterServiceData();
    },
    selectedRegions() {
      if (this.isSettingDefaultOptions) {
        return;
      }
      this.selectedRegionMap = {};
      for (const optId of this.selectedRegions) {
        this.selectedRegionMap[optId] = true;
      }
      this.filterServiceData();
    },
    selectedOperations() {
      if (this.isSettingDefaultOptions) {
        return;
      }
      this.selectedOperationMap = {};
      for (const optId of this.selectedOperations) {
        this.selectedOperationMap[optId] = true;
      }
      this.filterServiceData();
    },
    selectedInstanceTypes() {
      if (this.isSettingDefaultOptions) {
        return;
      }
      this.selectedInstanceTypeMap = {};
      for (const optId of this.selectedInstanceTypes) {
        this.selectedInstanceTypeMap[optId] = true;
      }
      this.filterServiceData();
    },
    selectedResourceIds() {
      if (this.isSettingDefaultOptions) {
        return;
      }
      this.selectedResourceIdMap = {};
      for (const optId of this.selectedResourceIds) {
        this.selectedResourceIdMap[optId] = true;
      }
      this.filterServiceData();
    },
  },
  methods: {
    ...mapMutations('billingReports', ['updateLogtime']),
    fetchReports() {
      this.$store.dispatch('billingReports/fetchCompanyReports', this.usageReport);
    },
    currencyType() {
      return this.vendor === 'aws' || this.vendor === 'azure' ? 'usd' : 'jpy';
    },
    handleExpandCollapseFilters() {
      this.showFilters = !this.showFilters;
    },
    currencySymbol(currency) {
      switch (currency) {
        case 'usd':
          return '$';
        case 'jpy':
          return '¥';
        default:
          return '';
      }
    },
    createFilterOptions() {
      const options = [];
      if (!this.allData) {
        return options;
      }
      let costGroupMap = {};
      let serviceMap = {};
      let regionMap = {};
      let operationMap = {};
      let instanceTypeMap = {};
      let resIdMap = {};
      for (const item of this.allData) {
        costGroupMap[item.customer_name] = true;
        for (const svc of item.services) {
          serviceMap[svc.service] = true;
          for (const rep of svc.report) {
            if (rep.region) {
              regionMap[rep.region] = true;
            }
            if (rep.operation) {
              operationMap[rep.operation] = true;
            }
            if (rep.instance_type) {
              instanceTypeMap[rep.instance_type] = true;
            }
            if (rep.resource_id) {
              resIdMap[rep.resource_id] = true;
            }
          }
        }
      }
      // Cost group
      let list = [];
      for (const costGroup in costGroupMap) {
        list.push({
          id: costGroup,
          value: costGroup,
          name: costGroup,
        });
      }
      this.costGroupOptions = sortObject(list, 'name');
      // Service
      list = [];
      for (const service in serviceMap) {
        list.push({
          id: service,
          value: service,
          name: service,
        });
      }
      this.serviceOptions = sortObject(list, 'name');
      // Region
      list = [];
      for (const region in regionMap) {
        list.push({
          id: region,
          value: region,
          name: region,
        });
      }
      this.regionOptions = sortObject(list, 'name');
      // Operation
      list = [];
      for (const operation in operationMap) {
        list.push({
          id: operation,
          value: operation,
          name: operation,
        });
      }
      this.operationOptions = sortObject(list, 'name');
      // Instance type
      list = [];
      for (const instanceType in instanceTypeMap) {
        list.push({
          id: instanceType,
          value: instanceType,
          name: instanceType,
        });
      }
      this.instanceTypeOptions = sortObject(list, 'name');
      // Resource id
      list = [];
      list.push({
        id: ' ',
        value: ' ',
        name: this.$t('Services without resource ID'),
      });
      for (const resId in resIdMap) {
        list.push({
          id: resId,
          value: resId,
          name: resId,
        });
      }
      this.resourceIdOptions = list;
      analyzer.track(`Perform: DashboardReportFilterTabFiltersComplete`, {
        groupBy: 'filters',
        logtime2: Number(Date.now()),
        interval2: Number(this.logtimeInterval),
      });
    },
    setDefaultSelectedOptions() {
      this.isSettingDefaultOptions = true;
      this.selectedCostGroups = [];
      for (const option of this.costGroupOptions) {
        this.selectedCostGroups.push(option.id);
        this.selectedCostGroupMap[option.id] = true;
      }
      this.selectedServices = [];
      for (const option of this.serviceOptions) {
        this.selectedServices.push(option.id);
        this.selectedServiceMap[option.id] = true;
      }
      this.selectedRegions = [];
      for (const option of this.regionOptions) {
        this.selectedRegions.push(option.id);
        this.selectedRegionMap[option.id] = true;
      }
      this.selectedOperations = [];
      for (const option of this.operationOptions) {
        this.selectedOperations.push(option.id);
        this.selectedOperationMap[option.id] = true;
      }
      this.selectedInstanceTypes = [];
      for (const option of this.instanceTypeOptions) {
        this.selectedInstanceTypes.push(option.id);
        this.selectedInstanceTypeMap[option.id] = true;
      }
      this.selectedResourceIds = [];
      for (const option of this.resourceIdOptions) {
        this.selectedResourceIds.push(option.id);
        this.selectedResourceIdMap[option.id] = true;
      }
      this.isSettingDefaultOptions = false;
      analyzer.track(`Perform: DashboardReportFilterTabFiltersComplete`, {
        groupBy: 'selectors',
        logtime2: Number(Date.now()),
        interval2: Number(this.logtimeInterval),
      });
    },
    filterServiceData() {
      if (!this.allData) {
        this.serviceData = [];
        return;
      }
      let resultMap = {};
      for (const item of this.allData) {
        if (this.selectedCostGroupMap && !this.selectedCostGroupMap[item.customer_name]) {
          continue;
        }
        for (const svc of item.services) {
          if (svc.service && this.selectedServiceMap && !this.selectedServiceMap[svc.service]) {
            continue;
          }
          let total = 0;
          let filteredReports = [];
          if (resultMap[svc.service]) {
            total = resultMap[svc.service].total; // initial total (sum up service total from other account)
          }
          for (const rep of svc.report) {
            if (rep.region && this.selectedRegionMap && !this.selectedRegionMap[rep.region]) {
              continue;
            }
            if (
              rep.operation &&
              this.selectedOperationMap &&
              !this.selectedOperationMap[rep.operation]
            ) {
              continue;
            }
            if (
              rep.instance_type &&
              this.selectedInstanceTypeMap &&
              !this.selectedInstanceTypeMap[rep.instance_type]
            ) {
              continue;
            }
            const resource_id = rep.resource_id ? rep.resource_id : ' ';
            if (this.selectedResourceIdMap && !this.selectedResourceIdMap[resource_id]) {
              continue;
            }
            total += rep.cost;
            rep.customer_name = item.customer_name;
            filteredReports.push(rep);
          }
          if (filteredReports.length === 0) {
            continue;
          }
          if (resultMap[svc.service]) {
            resultMap[svc.service].report.push(...filteredReports);
          } else {
            resultMap[svc.service] = {
              report: filteredReports,
              service: svc.service,
            };
          }
          resultMap[svc.service].total = total;
        }
      }
      let services = [];
      for (const svc in resultMap) {
        services.push(resultMap[svc]);
      }
      this.serviceData = services;
      analyzer.track(`Perform: DashboardReportFilterTabFiltersComplete`, {
        groupBy: 'services',
        logtime2: Number(Date.now()),
        interval2: Number(this.logtimeInterval),
      });
    },
    handleDownloadCsv() {
      // Convert to csv data.
      var csvData = [];
      for (const item of this.serviceData) {
        const serviceCode = item.service;
        for (const report of item.report) {
          csvData.push([
            report.customer_name,
            serviceCode,
            report.cost,
            report.region,
            report.usage_quantity,
            report.instance_type,
            report.operation,
            report.resource_id,
            report.description,
          ]);
        }
      }
      // Convert CSV data to String.
      var csvText = '';
      csvText += this.objectToCSVRow([
        'Id',
        'ServiceCode',
        'CostBeforeTax',
        'Region',
        'UsageQuantity',
        'InstanceType',
        'Operation',
        'ResourceID',
        'ItemDescription',
      ]);
      for (const item of csvData) {
        csvText += this.objectToCSVRow(item);
      }
      // Save file to local machine.
      var blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, `${this.selectedMonth}-report.csv`, { autoBom: true });
    },
    objectToCSVRow(dataObject) {
      var dataArray = new Array();
      for (var o in dataObject) {
        var innerValue = dataObject[o] === null ? '' : dataObject[o].toString();
        var result = innerValue.replace(/"/g, '""');
        result = '"' + result + '"';
        dataArray.push(result);
      }
      return dataArray.join(',') + '\r\n';
    },
  },
});
</script>

<style lang="scss" scoped>
.__ReportFilter {
  &-MoreInfo {
    font-size: $font-size-sm;
    padding-top: $space-xs;
    text-align: right;
  }
  .month-section {
    margin-top: 8px;
    display: flex;
    flex-direction: row;
  }
  .filter-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 8px;
    .horizontal-line {
      margin-left: 16px;
      height: 1px;
      background-color: #e0e0e0;
      flex-basis: fill;
      flex-grow: 1;
    }
  }
  .filters-container {
    display: flex;
    flex-grow: 1;
    flex-flow: row wrap;
    .filter-section {
      min-width: 400px;
    }
  }
}
</style>
